import { getDistanceFromAddress, findLocationCoordinates } from './gambianLocations';
import { geocodeAddressCached } from './osmGeocoding';

export interface DeliveryFeeResult {
  distanceKm: number;
  baseFee: number;
  distanceFee: number;
  totalFee: number;
  estimatedTime: number;
}

// Round to nearest 5 for easy cash change
function roundToNearest5(value: number): number {
  return Math.ceil(value / 5) * 5;
}

// Calculate delivery fee based on distance tiers
export function calculateDeliveryFee(distanceKm: number): DeliveryFeeResult {
  let totalFee: number;

  if (distanceKm <= 3) {
    totalFee = 80;
  } else if (distanceKm <= 7) {
    totalFee = 150;
  } else if (distanceKm <= 12) {
    totalFee = 200;
  } else {
    totalFee = 250;
  }

  // Estimate delivery time: 15 min base + 3 min per km
  const estimatedTime = Math.round(15 + (distanceKm * 3));

  return {
    distanceKm: Math.round(distanceKm * 100) / 100,
    baseFee: totalFee,
    distanceFee: 0,
    totalFee,
    estimatedTime
  };
}

// Calculate delivery fee between user location and restaurant address
export function calculateDeliveryFeeByLocation(
  userLat: number,
  userLng: number,
  restaurantAddress: string
): DeliveryFeeResult | null {
  try {
    // Use the new location lookup system
    const result = getDistanceFromAddress(userLat, userLng, restaurantAddress);
    
    if (result.distance > 0) {
      return calculateDeliveryFee(result.distance);
    }
    
    // Fallback if no distance calculated
    return getFallbackDeliveryFee();
  } catch (error) {
    console.error('Error calculating delivery fee by location:', error);
    return getFallbackDeliveryFee();
  }
}

// Calculate delivery fee from customer's typed address to restaurant address
export async function calculateDeliveryFeeFromAddress(
  customerAddress: string,
  restaurantAddress: string
): Promise<DeliveryFeeResult | null> {
  try {
    // Step 1: Look up restaurant coordinates from local database
    const restaurantCoords = findLocationCoordinates(restaurantAddress);
    if (!restaurantCoords) return null;

    // Step 2: Look up customer coordinates from local database
    const customerCoords = findLocationCoordinates(customerAddress);
    if (customerCoords) {
      const result = getDistanceFromAddress(
        customerCoords.latitude,
        customerCoords.longitude,
        restaurantAddress
      );
      if (result.distance > 0) {
        return calculateDeliveryFee(result.distance);
      }
    }

    // Step 3: Try OSM geocoding for customer address
    const geocoded = await geocodeAddressCached(customerAddress);
    if (geocoded) {
      const result = getDistanceFromAddress(
        geocoded.latitude,
        geocoded.longitude,
        restaurantAddress
      );
      if (result.distance > 0) {
        return calculateDeliveryFee(result.distance);
      }
    }

    return null;
  } catch (error) {
    console.error('Error calculating delivery fee from address:', error);
    return null;
  }
}

// Calculate delivery fee between two addresses (legacy function - now uses location lookup)
export async function calculateDeliveryFeeByAddress(
  fromAddress: string,
  toAddress: string
): Promise<DeliveryFeeResult | null> {
  try {
    // For now, use fallback since we're moving away from geocoding
    // This function can be deprecated in favor of calculateDeliveryFeeByLocation
    return getFallbackDeliveryFee();
  } catch (error) {
    console.error('Error calculating delivery fee by address:', error);
    return getFallbackDeliveryFee();
  }
}

// Fallback delivery fee when location lookup fails
export function getFallbackDeliveryFee(): DeliveryFeeResult {
  return {
    distanceKm: 5,
    baseFee: 150,
    distanceFee: 0,
    totalFee: 150,
    estimatedTime: 30
  };
}